import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaComments,
  FaTimes,
  FaMicrophone,
  FaVolumeUp,
  FaTrash,
  FaUserCircle,
  FaRobot,
} from "react-icons/fa";

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);

  const endRef = useRef();
  const inputRef = useRef();

  const getTime = () =>
    new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  useEffect(() => {
    setTimeout(() => {
      setMessages([
        {
          text: "Hi 👋 I'm AYSU AI Assistant",
          bot: true,
          time: getTime(),
        },
        {
          text: "Ask me about services, pricing or quotes 🚀",
          bot: true,
          time: getTime(),
        },
      ]);
    }, 500);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return alert("Voice not supported");

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.start();
    setListening(true);

    recognition.onresult = (e) => {
      setInput(e.results[0][0].transcript);
      setListening(false);
    };

    recognition.onerror = () => setListening(false);
  };

  const speak = (text) => {
    window.speechSynthesis.cancel();
    const speech = new SpeechSynthesisUtterance(text);
    speech.rate = 1;
    speech.pitch = 1;
    window.speechSynthesis.speak(speech);
  };

  const getReply = (msg) => {
    const text = msg.toLowerCase();

    if (text.includes("price") || text.includes("cost"))
      return "💰 Pricing depends on project size. Let's discuss on WhatsApp for exact quote.";

    if (text.includes("service"))
      return "🚀 We provide Web Development, Branding, AI Automation, Marketing & Design.";

    if (text.includes("contact"))
      return "📞 Call: +91 9361226606 or message us on WhatsApp.";

    if (text.includes("quote"))
      return "📩 Share your requirement, we’ll send a FREE quote soon.";

    if (text.includes("time"))
      return "⏰ We are available 24/7 for business support.";

    return "✨ Thanks for contacting AYSU GROUPS! We’ll help you grow digitally 🚀";
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMsg = {
      text: input,
      bot: false,
      time: getTime(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    setTimeout(() => {
      const replyText = getReply(userMsg.text);

      const botMsg = {
        text: replyText,
        bot: true,
        time: getTime(),
      };

      setMessages((prev) => [...prev, botMsg]);
      speak(replyText);
      setLoading(false);
    }, 700);
  };

  const quickReplies = ["Price", "Services", "Contact", "Quote"];

  return (
    <>
      {/* FLOAT BUTTON */}
      <motion.div
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-24 right-6 z-[9999]
        bg-gradient-to-br from-teal-500 via-cyan-500 to-emerald-500
        text-white p-4 rounded-full shadow-2xl cursor-pointer"
      >
        {open ? <FaTimes /> : <FaComments />}
      </motion.div>

      {/* CHAT WINDOW */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 80, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 80 }}
            className="fixed bottom-36 right-6 w-[380px] max-w-[95vw]
            rounded-3xl overflow-hidden 
            backdrop-blur-2xl bg-white/70 
            border border-white/30 
            shadow-[0_25px_70px_rgba(0,0,0,0.25)]
            flex flex-col z-[9999]"
          >
            {/* HEADER */}
            <div className="bg-gradient-to-r from-teal-500 via-cyan-500 to-emerald-500 text-white p-4 flex justify-between items-center font-semibold">
              <div className="flex items-center gap-2">
                <FaRobot />
                AYSU AI Assistant
              </div>

              <div className="flex gap-3">
                <FaTrash
                  className="cursor-pointer hover:scale-110 transition"
                  onClick={() => setMessages([])}
                />
                <FaTimes
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:scale-110 transition"
                />
              </div>
            </div>

            {/* MESSAGES */}
            <div className="p-4 h-80 overflow-y-auto space-y-3 bg-gradient-to-b from-white/60 to-white/30">
              {messages.length === 0 && (
                <div className="text-center text-gray-400 text-sm mt-10">
                  Start a conversation 🚀
                </div>
              )}

              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-end gap-2 ${
                    m.bot ? "justify-start" : "justify-end"
                  }`}
                >
                  {m.bot && (
                    <div className="w-7 h-7 flex items-center justify-center bg-teal-500 text-white rounded-full">
                      <FaRobot size={12} />
                    </div>
                  )}

                  <div
                    className={`px-4 py-2 rounded-2xl text-sm max-w-[75%] shadow-sm ${
                      m.bot
                        ? "bg-white border text-gray-700"
                        : "bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
                    }`}
                  >
                    {m.text}
                    <div className="text-[10px] opacity-60 mt-1 text-right">
                      {m.time}
                    </div>
                  </div>

                  {!m.bot && <FaUserCircle className="text-gray-500" />}
                </motion.div>
              ))}

              {loading && (
                <div className="flex gap-1 items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></span>
                </div>
              )}

              <div ref={endRef} />
            </div>

            {/* QUICK REPLIES */}
            <div className="flex gap-2 flex-wrap p-3 border-t bg-white/60">
              {quickReplies.map((q) => (
                <button
                  key={q}
                  onClick={() => {
                    setInput(q);
                    setTimeout(handleSend, 200);
                  }}
                  className="text-xs px-3 py-1 rounded-full bg-teal-100 text-teal-700 hover:scale-105 transition"
                >
                  {q}
                </button>
              ))}
            </div>

            {/* INPUT */}
            <div className="flex items-center border-t bg-white">
              <input
                ref={inputRef}
                className="flex-1 p-3 text-sm outline-none"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type message..."
              />

              <button onClick={startListening} className="px-2">
                <FaMicrophone
                  className={listening ? "text-red-500 animate-pulse" : ""}
                />
              </button>

              <button onClick={() => speak("Welcome to AYSU GROUPS")} className="px-2">
                <FaVolumeUp />
              </button>

              <button
                onClick={handleSend}
                className="bg-teal-500 text-white px-4 py-2 m-2 rounded-xl hover:scale-105 transition"
              >
                Send
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}